import { computed, DestroyRef, inject, ResourceRef, Service, Signal, signal, WritableSignal } from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';
import { legalEntityInitialState } from '../constants/constants';
import { SentryLogger } from '@lentau/core/sentry/shared/services/sentry-logger.service';
import { B2BService } from 'angular-api-gateway/v1/api/b2B.service';
import { defaultApiGatewayParams } from '@lentau/core/api/gateway';
import { ToastService } from '@lentau/ui-kit/molecules/toast/service/toast.service';
import { ToasterSuccessMessage } from '@lentau/ui-kit/molecules/toast/shared/toast.types';
import { PASSPORT_ID } from '@lentau/profile/organizations/legal-entity/shared/tokens/passport-id.token';
import { LEGAL_ENTITY_ID } from '@lentau/profile/organizations/legal-entity/shared/tokens/legal-entity-id.token';
import { LegalEntityData } from 'angular-api-gateway/v1/model/legalEntityData';
import { LegalEntityResponseManagerData } from 'angular-api-gateway/v1/model/legalEntityResponseManagerData';
import { PlatformService } from '@lentau/ui-kit/core/services/platform/platform.service';

@Service()
export class B2BLegalEntityStore {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly b2bService: B2BService = inject(B2BService);
  private readonly sentryLogger: SentryLogger = inject(SentryLogger);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly passportId: Signal<string> = inject(PASSPORT_ID);
  private readonly legalEntityId: Signal<string> = inject(LEGAL_ENTITY_ID);

  private readonly isRequested: WritableSignal<boolean> = signal<boolean>(false);

  private readonly legalEntityResource: ResourceRef<LegalEntityData | undefined> = rxResource<
    LegalEntityData,
    string | undefined
  >({
    params: (): string | undefined => {
      if (!this.isRequested() || !this.platformService.isBrowser || !this.passportId()) {
        return undefined;
      }
      return this.legalEntityId() || undefined;
    },
    stream: ({ params }: { params: string }): Observable<LegalEntityData> =>
      this.b2bService
        .getB2bLegalEntity({
          ...defaultApiGatewayParams,
          xOrganizationID: params,
          timestamp: new Date().getTime().toString(),
        })
        .pipe(
          tap((data: LegalEntityData): void => {
            this.sentryLogger.setUserData({ cardNumber: data.proCardNumber });
          })
        ),
  });

  public readonly legalEntity: Signal<LegalEntityData> = computed(
    (): LegalEntityData => this.legalEntityResource.value() ?? legalEntityInitialState.legalEntity
  );
  public readonly loading: Signal<boolean> = this.legalEntityResource.isLoading;

  public readonly email: Signal<string> = computed((): string => this.legalEntity().email);
  public readonly name: Signal<string> = computed((): string => this.legalEntity().name);
  public readonly phone: Signal<string> = computed((): string => this.legalEntity().phone);
  public readonly alias: Signal<string> = computed((): string => this.legalEntity().alias);
  public readonly proCardNumber: Signal<string> = computed((): string => this.legalEntity().proCardNumber);
  public readonly edo: Signal<boolean> = computed((): boolean => this.legalEntity().edo);
  public readonly managerData: Signal<LegalEntityResponseManagerData> = computed(
    (): LegalEntityResponseManagerData => this.legalEntity()?.manager?.data
  );
  public readonly regDate: Signal<number> = computed((): number => this.legalEntity().regDate);
  public readonly account: Signal<string> = computed((): string => this.legalEntity()?.bank?.account ?? '');

  public readonly alcoOwnNeedsPostSuccessCount: WritableSignal<number> = signal<number>(
    legalEntityInitialState.alcoOwnNeedsPostSuccessCount
  );
  public readonly alcoOwnNeedsPostErrorCount: WritableSignal<number> = signal<number>(
    legalEntityInitialState.alcoOwnNeedsPostErrorCount
  );
  public readonly alcoOwnNeedsPostErrorMessage: WritableSignal<string> = signal<string>(
    legalEntityInitialState.alcoOwnNeedsPostErrorMessage
  );

  public getLegalEntity(): void {
    if (!this.platformService.isBrowser || !this.passportId() || !this.legalEntityId()) {
      return;
    }
    if (this.isRequested()) {
      this.legalEntityResource.reload();
    } else {
      this.isRequested.set(true);
    }
  }

  public postLegalEntityAlcoOwnNeeds(): void {
    if (!(this.passportId() && this.legalEntityId())) {
      return;
    }

    this.b2bService
      .postLegalEntityAlcoOwnNeeds({
        ...defaultApiGatewayParams,
        xOrganizationID: this.legalEntityId(),
        timestamp: new Date().getTime().toString(),
        body: {},
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (): void => {
          this.toastService.show([new ToasterSuccessMessage('Подключено')]);
          this.alcoOwnNeedsPostSuccessCount.update((count: number) => count + 1);
        },
        error: (error: { Message?: string }[]): void => {
          this.alcoOwnNeedsPostErrorCount.update((count: number) => count + 1);
          this.alcoOwnNeedsPostErrorMessage.set(error?.[0]?.Message || 'Что-то пошло не так, попробуйте ещё раз');
        },
      });
  }
}
